import React from 'react';
import { motion } from 'framer-motion';
import { FiGithub, FiLinkedin, FiMail, FiExternalLink } from 'react-icons/fi';
import { SiFiverr, SiUpwork } from 'react-icons/si';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const navLinks = [
    { label: 'Home', href: '#hero' },
    { label: 'About', href: '#about' },
    { label: 'Skills', href: '#skills' },
    { label: 'Experience', href: '#experience' },
    { label: 'Services', href: '#services' },
    { label: 'Projects', href: '#projects' },
    { label: 'Contact', href: '#contact' },
  ];

  const socialLinks = [
    { icon: FiGithub, href: 'https://github.com/HARISMUGHAL', label: 'GitHub', color: 'hover:text-white' },
    { icon: FiLinkedin, href: 'https://www.linkedin.com/in/haris-mughal-183706341/', label: 'LinkedIn', color: 'hover:text-blue-400' },
    { icon: FiMail, href: 'mailto:harixmughal65@gmail.com', label: 'Email', color: 'hover:text-primary-500' },
    { icon: SiFiverr, href: 'https://www.fiverr.com/s/aexDjWp', label: 'Fiverr', color: 'hover:text-[#1dbf73]' },
    { icon: SiUpwork, href: 'https://www.upwork.com/freelancers/~016b95942fb99caccf', label: 'Upwork', color: 'hover:text-[#14a800]' },
  ];

  const handleNavClick = (e, href) => {
    e.preventDefault();
    const targetId = href.replace('#', '');
    const element = document.getElementById(targetId);
    if (element) {
      const navHeight = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - navHeight, behavior: 'smooth' });
    }
  };

  return (
    <footer className="relative border-t border-primary-500/10 bg-dark-900 overflow-hidden">
      {/* Subtle background glow */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-96 h-32 bg-primary-500/5 blur-3xl rounded-full" />

      <div className="container-custom relative z-10 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-10">

          {/* Brand Column */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-dark-900 font-black text-sm">
                HK
              </div>
              <div>
                <p className="font-bold text-white text-sm">HARIS KAMRAN</p>
                <p className="text-xs text-primary-500 font-semibold">AI Engineer & CTO</p>
              </div>
            </div>
            <p className="text-sm text-white/50 leading-relaxed max-w-xs">
              AI &amp; ML Engineer, Full Stack Developer, and Startup CTO based in Islamabad, Pakistan. Open to remote opportunities worldwide.
            </p>
            {/* Startup Link */}
            <motion.a
              href="https://zynqoraedge.com"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ x: 4 }}
              className="inline-flex items-center gap-2 text-sm text-accent-500 hover:text-primary-500 font-semibold transition-colors"
            >
              <FiExternalLink className="text-xs" />
              zynqoraedge.com
            </motion.a>
          </div>

          {/* Navigation Column */}
          <div className="space-y-4">
            <h4 className="text-sm font-bold text-white uppercase tracking-wider">Navigation</h4>
            <nav className="grid grid-cols-2 gap-2">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="text-sm text-white/50 hover:text-primary-500 transition-colors"
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </div>

          {/* Connect Column */}
          <div className="space-y-4">
            <h4 className="text-sm font-bold text-white uppercase tracking-wider">Connect</h4>
            <div className="space-y-2">
              <a href="mailto:harixmughal65@gmail.com" className="flex items-center gap-2 text-sm text-white/50 hover:text-primary-500 transition-colors">
                <FiMail className="text-primary-500" />
                harixmughal65@gmail.com
              </a>
              <p className="flex items-center gap-2 text-sm text-white/50">
                <span className="text-primary-500">📍</span>
                Islamabad, Pakistan
              </p>
            </div>
            <div className="flex gap-3 pt-1">
              {socialLinks.map((social, idx) => {
                const Icon = social.icon;
                return (
                  <motion.a
                    key={idx}
                    href={social.href}
                    target={social.href.startsWith('mailto:') ? '_self' : '_blank'}
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    whileHover={{ scale: 1.2, y: -3 }}
                    className={`p-2 rounded-lg bg-dark-800/50 border border-primary-500/10 text-white/40 ${social.color} hover:border-primary-500/30 transition-all duration-300`}
                  >
                    <Icon className="text-base" />
                  </motion.a>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-primary-500/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/30 text-center sm:text-left">
            © {currentYear} Muhammad Haris Kamran. All rights reserved.
          </p>
          <p className="text-xs text-white/30 text-center sm:text-right">
            Built with <span className="text-primary-500">React</span> &amp; <span className="text-accent-500">Framer Motion</span>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
